import { useMemo, useRef, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { fromLonLat } from 'ol/proj';
import { X, Save, Trash2, Settings2, Type } from 'lucide-react';
import { toast } from 'sonner';
import { useMapStore } from '@/stores/mapStore';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DynamicSchemaFields } from '@/components/shared/DynamicSchemaFields';
import { useMapOverlayPosition } from '../../hooks/useMapOverlayPosition';
import { LayerSchemaConfigProperty } from '@packages/database/types';
import { cn } from '@/lib/utils';
import { useMobile } from '@/hooks/useMobile';

// ─── Shell ────────────────────────────────────────────────────────────────────
// Wraps content in either a floating card (desktop) or bottom drawer (mobile).

const PopupShell = ({
  children,
  isMobile,
  popupRef,
}: {
  children: React.ReactNode;
  isMobile: boolean;
  popupRef: React.RefObject<HTMLDivElement | null>;
}) => {
  if (isMobile) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-[9999] px-3 pb-4 pointer-events-auto">
        <div
          className={cn(
            'w-full rounded-xl border border-border bg-card shadow-2xl overflow-hidden',
            'animate-in slide-in-from-bottom-4 duration-200',
          )}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={popupRef}
      className="absolute top-0 left-0 z-[9999] will-change-transform pointer-events-none"
      style={{ display: 'none' }}
    >
      <div
        className={cn(
          'pointer-events-auto mb-2 w-72',
          'animate-in fade-in zoom-in-95 duration-150',
        )}
      >
        <div className="rounded-xl border border-border bg-card shadow-2xl overflow-hidden">
          {children}
        </div>
      </div>
    </div>
  );
};

// ─── Shared header ────────────────────────────────────────────────────────────

const PopupHeader = ({
  icon: Icon,
  title,
  onClose,
}: {
  icon: React.ElementType;
  title: string;
  onClose: () => void;
}) => (
  <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30 shrink-0">
    <div className="flex items-center gap-1.5 min-w-0">
      <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <span className="text-[11px] font-semibold text-foreground truncate">
        {title}
      </span>
    </div>
    <button
      type="button"
      onClick={onClose}
      className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors shrink-0"
    >
      <X className="w-3 h-3" />
    </button>
  </div>
);

// ─── Shared footer ────────────────────────────────────────────────────────────

const PopupFooter = ({
  onDelete,
  onSave,
  saveDisabled,
  saveLabel = 'Save',
  deleteLabel = 'Remove',
  onCancel,
  cancelLabel,
}: {
  onDelete?: () => void;
  onSave: () => void;
  saveDisabled?: boolean;
  saveLabel?: string;
  deleteLabel?: string;
  onCancel?: () => void;
  cancelLabel?: string;
}) => (
  <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-border bg-muted/20 shrink-0">
    <div>
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="flex items-center gap-1 text-[11px] text-destructive hover:text-destructive/80 transition-colors h-6 px-1.5 rounded hover:bg-destructive/10"
        >
          <Trash2 className="w-3 h-3" />
          {deleteLabel}
        </button>
      )}
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="text-[11px] text-muted-foreground hover:text-foreground transition-colors h-6 px-1.5 rounded hover:bg-muted/60"
        >
          {cancelLabel ?? 'Cancel'}
        </button>
      )}
    </div>
    <button
      type="button"
      onClick={onSave}
      disabled={saveDisabled}
      className="flex items-center gap-1 text-[11px] font-medium h-6 px-3 rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-40"
    >
      <Save className="w-3 h-3" />
      {saveLabel}
    </button>
  </div>
);

// ─── Label input form ─────────────────────────────────────────────────────────

const LabelInputForm = ({ featureId }: { featureId: string }) => {
  const { redlineData, updateRedlineData, setActiveSketchFeatureId } =
    useMapStore();
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []);

  const handleSave = () => {
    if (!text.trim() || !redlineData) {
      updateRedlineData({
        ...redlineData!,
        features: redlineData!.features.filter((f: any) => f.id !== featureId),
      } as any);
      setActiveSketchFeatureId(null);
      return;
    }
    updateRedlineData({
      ...redlineData,
      features: redlineData.features.map((f: any) =>
        f.id === featureId
          ? {
              ...f,
              properties: { ...f.properties, label: text, isPending: false },
            }
          : f,
      ),
    } as any);
    toast.success('Label added');
    setActiveSketchFeatureId(null);
  };

  const handleCancel = () => {
    if (!redlineData) return;
    updateRedlineData({
      ...redlineData,
      features: redlineData.features.filter((f: any) => f.id !== featureId),
    } as any);
    setActiveSketchFeatureId(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') handleCancel();
  };

  return (
    <>
      <PopupHeader icon={Type} title="Add Label" onClose={handleCancel} />
      <div className="px-3 py-3">
        <Input
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter label text..."
          className="h-7 text-xs"
        />
        <p className="text-[10px] text-muted-foreground mt-1.5">
          Enter to save · Esc to cancel
        </p>
      </div>
      <PopupFooter
        onCancel={handleCancel}
        cancelLabel="Cancel"
        onSave={handleSave}
        saveDisabled={!text.trim()}
        saveLabel="Save Label"
      />
    </>
  );
};

// ─── Attribute form ───────────────────────────────────────────────────────────

const AttributeForm = ({ featureId }: { featureId: string }) => {
  const {
    redlineData,
    updateRedlineData,
    setActiveSketchFeatureId,
    activeRedlineTool,
    layers,
  } = useMapStore();

  const activeFeature = useMemo(
    () => redlineData?.features.find((f: any) => f.id === featureId),
    [redlineData, featureId],
  );

  const assetType = activeFeature?.properties?.assetType ?? activeRedlineTool;

  // Two-pass layer lookup: first by sourceFilter.assetType (assets),
  // then by layer key (premises and other keyed layers).
  const layerDef = useMemo(
    () =>
      layers.find(
        (l) =>
          l.sourceTable === 'assets' &&
          (l.sourceFilter as any)?.assetType === assetType,
      ) ??
      layers.find((l) => l.key === assetType) ??
      null,
    [layers, assetType],
  );

  const formFields: LayerSchemaConfigProperty[] = useMemo(
    () => layerDef?.schemaConfig?.properties?.filter((p) => p.showInForm) ?? [],
    [layerDef],
  );

  const currentValues = useMemo(
    () =>
      Object.fromEntries(
        formFields.map((f) => [
          f.alias,
          activeFeature?.properties?.[f.alias] ?? '',
        ]),
      ),
    [activeFeature, formFields],
  );

  const methods = useForm({
    values: currentValues,
    resetOptions: { keepDirtyValues: true },
  });

  const {
    handleSubmit,
    setFocus,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (formFields.length > 0) {
      const t = setTimeout(() => setFocus(formFields[0].alias as any), 100);
      return () => clearTimeout(t);
    }
  }, [formFields, setFocus]);

  const onSave = (data: Record<string, any>) => {
    if (!redlineData) return;
    updateRedlineData({
      ...redlineData,
      features: redlineData.features.map((f: any) =>
        f.id === featureId
          ? { ...f, properties: { ...f.properties, ...data } }
          : f,
      ),
    } as any);
    toast.success('Attributes saved');
    setActiveSketchFeatureId(null);
  };

  const onDelete = () => {
    if (!redlineData) return;
    updateRedlineData({
      ...redlineData,
      features: redlineData.features.filter((f: any) => f.id !== featureId),
    } as any);
    setActiveSketchFeatureId(null);
    toast.info('Feature removed');
  };

  const title = layerDef?.displayName
    ? `${layerDef.displayName} Attributes`
    : 'Feature Attributes';

  return (
    <FormProvider {...methods}>
      <PopupHeader
        icon={Settings2}
        title={title}
        onClose={() => setActiveSketchFeatureId(null)}
      />
      <div
        className="overflow-hidden"
        style={{ height: '280px', maxHeight: '45vh' }}
      >
        <ScrollArea className="h-full w-full">
          <div className="px-3 py-3 space-y-3">
            {formFields.length === 0 ? (
              <p className="text-[11px] text-muted-foreground text-center py-8">
                No editable fields configured for this layer.
              </p>
            ) : (
              <DynamicSchemaFields fields={formFields} />
            )}
          </div>
        </ScrollArea>
      </div>
      <PopupFooter
        onDelete={onDelete}
        onSave={handleSubmit(onSave)}
        saveDisabled={isSubmitting}
      />
    </FormProvider>
  );
};

// ─── Popup wrapper ────────────────────────────────────────────────────────────

export const RedlineAttributePopup = () => {
  const popupRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useMobile();
  const { redlineData, activeSketchFeatureId } = useMapStore();

  const activeFeature = useMemo(
    () =>
      redlineData?.features.find((f: any) => f.id === activeSketchFeatureId),
    [redlineData, activeSketchFeatureId],
  );

  const position = useMemo(() => {
    if (!activeFeature) return null;
    const geom = activeFeature.geometry;
    if (geom.type === 'Point') {
      return fromLonLat(geom.coordinates as [number, number]);
    }
    if (geom.type === 'LineString') {
      const coords = geom.coordinates as number[][];
      return fromLonLat(
        coords[Math.floor(coords.length / 2)] as [number, number],
      );
    }
    if (geom.type === 'Polygon') {
      const coords = geom.coordinates[0] as number[][];
      return fromLonLat(
        coords[Math.floor(coords.length / 2)] as [number, number],
      );
    }
    return null;
  }, [activeFeature]);

  useMapOverlayPosition({ elementRef: popupRef, position });

  if (!activeFeature || !position) return null;

  const isPendingLabel =
    activeFeature.properties?.assetType === 'LABEL' &&
    activeFeature.properties?.isPending === true;

  const content = isPendingLabel ? (
    <LabelInputForm
      key={activeSketchFeatureId!}
      featureId={activeSketchFeatureId!}
    />
  ) : (
    <AttributeForm
      key={activeSketchFeatureId!}
      featureId={activeSketchFeatureId!}
    />
  );

  return (
    <PopupShell isMobile={isMobile} popupRef={popupRef}>
      {content}
    </PopupShell>
  );
};
